import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  DialogContentText
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Security as ModeratorIcon,
  Group as MemberIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useGroups } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export default function GroupMemberManagement({ open, onClose, group }) {
  const { currentUser } = useAuth();
  const { 
    removeMemberFromGroup, 
    updateMemberRole, 
    sendInvitation,
    cancelInvitation,
    resendInvitation,
    isUserAdmin,
    getUserRole
  } = useGroups();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Invite user state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [sendingInvite, setSendingInvite] = useState(false);
  

  
  // Role editing state
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState('member');

  // Confirmation dialogs
  const [cancelInviteDialog, setCancelInviteDialog] = useState({ open: false, invite: null });
  const [resendInviteDialog, setResendInviteDialog] = useState({ open: false, invite: null });

  // User names state
  const [userNames, setUserNames] = useState({});
  const [loadingNames, setLoadingNames] = useState(false);
  const isFirstOpen = useRef(true);

  const canManageMembers = isUserAdmin(group?.id);
  const userRole = getUserRole(group?.id);



  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminIcon fontSize="small" />;
      case 'moderator':
        return <ModeratorIcon fontSize="small" />;
      default:
        return <MemberIcon fontSize="small" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'moderator':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'moderator':
        return 'Moderator';
      default:
        return 'Member';
    }
  };

  const getInvitationStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ScheduleIcon fontSize="small" />;
      case 'accepted':
        return <InfoIcon fontSize="small" />;
      case 'declined':
        return <InfoIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getInvitationStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setSendingInvite(true);
    setError('');
    setSuccess('');

    try {
      // Send invitation instead of directly adding member
      const message = `You've been invited to join ${group.name} as a ${getRoleLabel(inviteRole)}.`;
      await sendInvitation(group.id, inviteEmail.trim(), message);
      setSuccess(`Invitation sent to ${inviteEmail} for role: ${getRoleLabel(inviteRole)}`);
      setInviteEmail('');
      setInviteRole('member');
    } catch (error) {
      setError(error.message);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await removeMemberFromGroup(group.id, userId);
      setSuccess('Member removed successfully');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateMemberRole(group.id, userId, newRole);
      setSuccess('Member role updated successfully');
      setEditingRole(null);
      setNewRole('member');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };







  const handleCancelInvitation = async (invite) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      console.log('Cancelling invitation:', invite);
      console.log('Invitation ID:', invite.id);
      
      if (!invite.id) {
        throw new Error('Invalid invitation ID');
      }
      
      console.log('Before cancelling invitation - activeTab:', activeTab);
      await cancelInvitation(invite.id);
      console.log('After cancelling invitation - activeTab:', activeTab);
      setSuccess(`Invitation to ${invite.email} cancelled successfully`);
      setCancelInviteDialog({ open: false, invite: null });
      
      // Note: GroupContext already updates local state, so no need to refresh
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invite) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await resendInvitation(invite.id);
      setSuccess(`Invitation to ${invite.email} resent successfully`);
      setResendInviteDialog({ open: false, invite: null });
      
      // Note: GroupContext already updates local state, so no need to refresh
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyInvitationLink = (invite) => {
    // This would generate a shareable invitation link
    navigator.clipboard.writeText(`${window.location.origin}/invite/${invite.id}`);
    setSuccess('Invitation link copied to clipboard');
  };

  const startRoleEdit = (userId, currentRole) => {
    setEditingRole(userId);
    setNewRole(currentRole);
  };

  const cancelRoleEdit = () => {
    setEditingRole(null);
    setNewRole('member');
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Function to fetch user names
  const fetchUserNames = useCallback(async (memberIds) => {
    if (!memberIds || memberIds.length === 0) return;
    
    setLoadingNames(true);
    try {
      const names = {};
      for (const memberId of memberIds) {
        // Try to get from current user first
        if (memberId === currentUser?.uid) {
          names[memberId] = currentUser.displayName || currentUser.email || 'You';
          continue;
        }
        
        // Fetch user data from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            names[memberId] = userData.displayName || userData.email || 'Unknown User';
          } else {
            names[memberId] = 'Unknown User';
          }
        } catch (error) {
          console.error(`Error fetching user ${memberId}:`, error);
          names[memberId] = 'Unknown User';
        }
      }
      setUserNames(names);
    } catch (error) {
      console.error('Error fetching user names:', error);
    } finally {
      setLoadingNames(false);
    }
  }, [currentUser]);

  useEffect(() => {
    console.log('GroupMemberManagement useEffect - open:', open, 'activeTab:', activeTab);
    if (open) {
      clearMessages();
      // Only reset active tab if it's the first time opening (not on re-renders)
      if (isFirstOpen.current) {
        setActiveTab(0);
        isFirstOpen.current = false;
        console.log('First time opening - set activeTab to 0');
      }
      // Fetch user names when component opens
      if (group?.members) {
        fetchUserNames(group.members);
      }
    } else {
      // Reset the ref when dialog closes
      isFirstOpen.current = true;
      console.log('Dialog closed - reset isFirstOpen');
    }
  }, [open, group?.members, activeTab, fetchUserNames]);

  console.log('GroupMemberManagement render - group:', group?.id, 'open:', open, 'activeTab:', activeTab);
  
  if (!group) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MemberIcon />
          Manage Members - {group.name}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
            {success}
          </Alert>
        )}

        {/* Group Info */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Group Capacity:</strong> {group.memberCount}/{group.maxMembers || 50} members
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Your Role:</strong> {getRoleLabel(userRole)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Pending Invitations:</strong> {group.pendingInvites?.length || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Group Created:</strong> {group.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs for different sections */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="member management tabs">
            <Tab label="Members" icon={<PersonIcon />} iconPosition="start" />
            <Tab label="Invitations" icon={<EmailIcon />} iconPosition="start" />
            <Tab label="Invite Users" icon={<AddIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Members Tab */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Current Members ({group.memberCount})
            </Typography>
            
            <List>
              {group.members.map((memberId, index) => {
                const isCreator = memberId === group.createdBy;
                const memberRole = group.memberRoles?.[memberId] || 'member';
                const canEditRole = canManageMembers && !isCreator && memberId !== currentUser.uid;
                const isEditing = editingRole === memberId;
                
                return (
                  <React.Fragment key={memberId}>
                    <ListItem>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">
                              {isCreator 
                                ? (group.createdBy === currentUser.uid ? 'You' : 'Group Creator')
                                : (loadingNames ? 'Loading...' : (userNames[memberId] || `Member ${index + 1}`))
                              }
                            </Typography>
                            <Chip
                              icon={getRoleIcon(memberRole)}
                              label={getRoleLabel(memberRole)}
                              size="small"
                              color={getRoleColor(memberRole)}
                              variant="outlined"
                            />
                            {isCreator && (
                              <Chip
                                label="Creator"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {/* User ID hidden for privacy */}
                          </Typography>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        {canEditRole && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {isEditing ? (
                              <>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                    size="small"
                                  >
                                    <MenuItem value="member">Member</MenuItem>
                                    <MenuItem value="moderator">Moderator</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                  </Select>
                                </FormControl>
                                
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleUpdateRole(memberId)}
                                  disabled={loading}
                                >
                                  Save
                                </Button>
                                
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={cancelRoleEdit}
                                  disabled={loading}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Tooltip title="Edit Role">
                                <IconButton
                                  size="small"
                                  onClick={() => startRoleEdit(memberId, memberRole)}
                                  disabled={loading}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            <Tooltip title="Remove Member">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(memberId)}
                                disabled={loading}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    {index < group.members.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
            
            {group.members.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No members yet. Add some members to get started!
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Invitations Tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Invitation Management
            </Typography>
            
            {group.pendingInvites && group.pendingInvites.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Manage pending invitations ({group.pendingInvites.length} total):
                </Typography>
                
                <Grid container spacing={2}>
                  {group.pendingInvites.map((invite, index) => {
                    console.log('Rendering invite:', invite);
                    return (
                    <Grid item xs={12} sm={6} md={4} key={invite.id || index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" noWrap>
                              {invite.email}
                            </Typography>
                          </Box>
                          
                          {invite.message && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              "{invite.message}"
                            </Typography>
                          )}
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <ScheduleIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Sent: {invite.invitedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                            </Typography>
                          </Box>
                          
                          <Chip 
                            icon={getInvitationStatusIcon(invite.status || 'pending')}
                            label={invite.status || 'pending'} 
                            size="small" 
                            color={getInvitationStatusColor(invite.status || 'pending')} 
                            variant="outlined"
                          />
                        </CardContent>
                        
                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Resend Invitation">
                              <IconButton
                                size="small"
                                onClick={() => setResendInviteDialog({ open: true, invite })}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Copy Invitation Link">
                              <IconButton
                                size="small"
                                onClick={() => copyInvitationLink(invite)}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          
                                                      <Tooltip title="Cancel Invitation">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  console.log('Cancel button clicked for invite:', invite);
                                  setCancelInviteDialog({ open: true, invite });
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
                </Grid>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No pending invitations. Send some invitations to see them here!
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Add/Invite Tab */}
        {activeTab === 2 && (
          <>
            {/* Add Member Section */}
            {canManageMembers && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Invite Existing User
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send an invitation to an existing user by their email address. They'll receive an invitation to join this group.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <TextField
                    label="Email Address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                    size="small"
                    sx={{ minWidth: 250 }}
                    disabled={sendingInvite}
                  />
                  
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={inviteRole}
                      label="Role"
                      onChange={(e) => setInviteRole(e.target.value)}
                      disabled={sendingInvite}
                    >
                      <MenuItem value="member">Member</MenuItem>
                      <MenuItem value="moderator">Moderator</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="contained"
                    startIcon={sendingInvite ? <CircularProgress size={16} /> : <AddIcon />}
                    onClick={handleInviteUser}
                    disabled={sendingInvite || !inviteEmail.trim()}
                  >
                    {sendingInvite ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </Box>
              </Paper>
            )}



                           {/* Information Section */}
              <Paper sx={{ p: 3, backgroundColor: 'info.50' }}>
                <Typography variant="h6" gutterBottom color="info.main">
                  <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Invitation Guidelines
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>Users cannot receive multiple pending invitations to the same group</li>
                  <li>Invitations expire after 30 days (if not accepted)</li>
                  <li>You can resend invitations or cancel them at any time</li>
                  <li>Invitations are managed through the Invitations tab</li>
                </Box>
              </Paper>
           </>
         )}
       </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      {/* Cancel Invitation Confirmation Dialog */}
      <Dialog
        open={cancelInviteDialog.open}
        onClose={() => setCancelInviteDialog({ open: false, invite: null })}
      >
        <DialogTitle>Cancel Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel the invitation to <strong>{cancelInviteDialog.invite?.email}</strong>? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelInviteDialog({ open: false, invite: null })}>
            No, Keep It
          </Button>
          <Button onClick={() => handleCancelInvitation(cancelInviteDialog.invite)} color="error" variant="contained">
            Yes, Cancel Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Invitation Confirmation Dialog */}
      <Dialog
        open={resendInviteDialog.open}
        onClose={() => setResendInviteDialog({ open: false, invite: null })}
      >
        <DialogTitle>Resend Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to resend the invitation to <strong>{resendInviteDialog.invite?.email}</strong>? 
            This will send a new invitation email.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendInviteDialog({ open: false, invite: null })}>
            Cancel
          </Button>
          <Button onClick={() => handleResendInvitation(resendInviteDialog.invite)} color="primary" variant="contained">
            Yes, Resend
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
